import { JSX, ReactNode } from 'react';
import {
    Box,
    IconButton,
    Tooltip,
    Typography
} from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import { eyebrow as eyebrowSx } from './styles/dashboard-tokens';

/**
 * Shared minimum height, so every card lines up whether it is loading, failed or loaded. Was
 * previously three separate hardcoded 405s (two card rules plus each widget's loading box), which
 * is why the grid used to shift as data landed.
 */
export const CARD_MIN_HEIGHT: number = 405;

type DashboardCardProps = {
    title: string,
    /** Uppercase category label above the title. Groups cards without adding chrome. */
    eyebrow?: string,
    /** Header-right slot: reload buttons, the pager, counts. */
    actions?: ReactNode,
    /** A figure or status line under the title — the card's answer at a glance. */
    meta?: ReactNode,
    children: ReactNode,
    /** Exposed as aria-busy, so the loading state is announced rather than just drawn. */
    busy?: boolean,
    /** Clip the body. Cards holding a scrolling table need it; chart cards do not. */
    clip?: boolean,
    /** Override the shared minimum height for cards whose content is naturally shorter. */
    minHeight?: number,
    /** Per-widget hook for body rules that stay in SCSS. */
    className?: string,
}

/**
 * The one card shell every dashboard widget renders inside.
 *
 * Chrome lives here as MUI `sx` rather than SCSS specifically so it can read palette tokens:
 * the old `border: 1px solid #eee` vanished under the dark theme that AdminApp registers.
 */
export const DashboardCard: (props: DashboardCardProps) => JSX.Element = (
    {
        title,
        eyebrow,
        actions,
        meta,
        children,
        busy = false,
        clip = false,
        minHeight = CARD_MIN_HEIGHT,
        className = ''
    }: DashboardCardProps
): JSX.Element => {
    return (
        <Box
            className={ className }
            aria-busy={ busy }
            sx={ {
                width: '100%',
                // Fill the grid row rather than only the content. Grid items stretch by
                // default, so two cards side by side then end level with each other however
                // many rows each table has. `minHeight` still sets the floor for a card that
                // is alone in its row.
                height: '100%',
                minHeight,
                p: 2.5,
                borderRadius: 2,
                border: 1,
                borderColor: 'divider',
                bgcolor: 'background.paper',
                display: 'flex',
                flexDirection: 'column',
                overflow: clip ? 'hidden' : 'visible',
            } }
        >
            <Box
                sx={ {
                    width: '100%',
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 2,
                } }
            >
                <Box sx={ { minWidth: 0 } }>
                    { eyebrow !== undefined &&
                        <Typography component="p" sx={ eyebrowSx }>{ eyebrow }</Typography>
                    }
                    <Typography
                        variant="subtitle1"
                        component="h2"
                        sx={ { fontWeight: 600, fontSize: 15, lineHeight: 1.4 } }
                    >
                        { title }
                    </Typography>
                </Box>
                { actions !== undefined &&
                    <Box sx={ { display: 'flex', alignItems: 'center', flexShrink: 0 } }>
                        { actions }
                    </Box>
                }
            </Box>

            { meta !== undefined &&
                <Box sx={ { mt: 1.5 } }>{ meta }</Box>
            }

            { /* `width: 0` + `minWidth: '100%'` turns the body into a genuine scroll container:
                 it reports zero intrinsic width upward while still filling the card. That is what
                 stops a wide datagrid stretching the entire page, since React Admin's Layout root
                 is `min-width: fit-content` (see ClaudeCode-Notes.md). Note that `overflow-x`
                 alone does NOT achieve this, nor does `contain: inline-size` — both were measured
                 and both still leaked. */ }
            <Box
                sx={ {
                    flex: 1,
                    width: 0,
                    minWidth: '100%',
                    mt: 2,
                    overflowX: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                } }
            >
                { children }
            </Box>
        </Box>
    );
}

type DashboardCardErrorProps = {
    message: string,
    onRetry: () => void,
}

/** Body content for a card whose data failed to load. Keeps the card's chrome and offers a retry. */
export const DashboardCardError: (props: DashboardCardErrorProps) => JSX.Element = (
    { message, onRetry }: DashboardCardErrorProps
): JSX.Element => {
    return (
        <Box
            sx={ {
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                textAlign: 'center',
            } }
        >
            <Typography variant="body2" color="text.secondary">{ message }</Typography>
            <Tooltip title="Retry">
                <IconButton aria-label="Retry" onClick={ onRetry }>
                    <ReplayIcon />
                </IconButton>
            </Tooltip>
        </Box>
    );
}
